"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Compass, Scale, ShieldCheck, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
interface FoundationPillar {
  id: string;
  numeral: string;
  title: string;
  description: string;
  icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  href: string;
}
const pillars: FoundationPillar[] = [{
  id: "legacy",
  numeral: "01",
  title: "Family Legacy",
  description: "Building enduring legacies through shared travel experiences that resonate across time",
  icon: Compass,
  href: "/AnchorOrbitMethodPage"
}, {
  id: "harmony",
  numeral: "02",
  title: "Generational Harmony",
  description: "Expertly balancing group togetherness with individual freedom for affluent families",
  icon: Scale,
  href: "/AnchorOrbitMethodPage"
}, {
  id: "precision",
  numeral: "03",
  title: "Logistical Precision",
  description: "Eliminating friction through high-touch curation and proprietary frameworks",
  icon: ShieldCheck,
  href: "/AnchorOrbitMethodPage"
}];
export default function Foundations() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1] as const
      }
    }
  };
  return <section id="three-foundations-grid" data-nav="light" className="bg-background text-foreground relative py-16 md:py-24 border-b border-border">
      <div className="container mx-auto max-w-[1320px] px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-4">
            <span className="h-px w-6 bg-border" />
            <span className="font-sans text-xs uppercase tracking-widest text-muted-foreground font-semibold">
              The Architecture
            </span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-foreground leading-[1.2]">
            The architectural foundations of milestone journeys
          </h2>
          <p className="mt-4 font-sans text-base sm:text-lg text-foreground/80 leading-relaxed">
            Three core disciplines shape every Anchor & Orbit™ itinerary, balancing shared memories with personal space.
          </p>
        </div>

        {/* 3-Column Pillars Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {pillars.map((pillar, index) => {
          const IconComponent = pillar.icon;
          return <motion.div key={pillar.id} data-index={index} variants={cardVariants} className="group relative flex flex-col justify-between bg-card text-card-foreground p-8 sm:p-10 border border-border rounded-sm shadow-xs transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-accent-foreground/40 hover:shadow-md">
                <div>
                  {/* Top Bar: Icon + Number Pill */}
                  <div className="flex items-center justify-between gap-4 mb-8">
                    <div className="flex size-12 items-center justify-center rounded-sm bg-muted text-foreground transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                      <IconComponent className="size-6 stroke-[1.25]" strokeWidth={1.25} />
                    </div>
                    <span className="inline-flex items-center justify-center rounded-full bg-primary/10 px-3 py-1 font-sans text-xs font-semibold uppercase tracking-wider text-primary">
                      {pillar.numeral}
                    </span>
                  </div>

                  {/* Headline & Body */}
                  <h3 className="font-serif text-xl sm:text-2xl font-semibold tracking-tight text-foreground mb-4">
                    {pillar.title}
                  </h3>
                  <p className="font-sans text-sm sm:text-base text-foreground/85 leading-relaxed">
                    {pillar.description}
                  </p>
                </div>

                {/* Subtle Pillar Link Footer */}
                <div className="mt-8 pt-6 border-t border-border/60">
                  <Link to={pillar.href} className="inline-flex items-center gap-2 font-sans text-xs font-semibold uppercase tracking-wider text-primary group-hover:text-primary transition-colors">
                    <span>Explore methodology</span>
                    <ArrowRight className="size-3.5 transition-transform duration-200 group-hover:translate-x-1" />
                  </Link>
                </div>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}